import * as React from 'react';
import cn from 'clsx';
import { ArticleHeading2, ArticleParagraph1, UIText1 } from '@cian/ui-kit';

import { withDeviceType } from '../../../../utils/withDeviceType';
import { DealStepModalHeadingSection } from '../DealStepModalHeadingSection';
import { DealStepModalCard } from '../DealStepModalCard';
import { DealStepModalCardList } from '../DealStepModalCardList';
import { NBSP } from '../../../../constants/symbols';
import { IDealStepsModalContentProps } from '../../types';

import * as styles from './styles.css';

const DocumentSigningStepModalContent: React.FC<React.PropsWithChildren<IDealStepsModalContentProps>> = props => {
  return (
    <>
      <DealStepModalHeadingSection
        imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/e_signature_x1.png"
        imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/e_signature_x2.png"
        chip="В среднем 30 минут"
        title={props.title}
        subtitle="Мы обеспечиваем полностью безопасный процесс для всех участников сделки."
      />
      <ol className={cn(styles['content'], styles[props.deviceType])}>
        <li className={cn(styles['step'], styles[props.deviceType])}>
          <ArticleHeading2 as="h5">1. Выпуск электронных подписей</ArticleHeading2>
          <UIText1 as="p">
            Персональный помощник выпустит усиленную электронную квалифицированную подпись всем участникам сделки.
            Активируйте подпись перед использованием любым из способов:
          </UIText1>
          <DealStepModalCardList>
            <DealStepModalCard
              imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/nfc_x1.png"
              imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/nfc_x2.png"
              onClick={() => props.onCardClick('foreignPassportScan')}
            >
              Удалённо: через загранпаспорт и смартфон
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/courier_x1.png"
              imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/courier_x2.png"
              onClick={() => props.onCardClick('courierDelivery')}
            >
              Доставка представителем удостоверяющего центра
            </DealStepModalCard>
          </DealStepModalCardList>
        </li>

        <li className={cn(styles['step'], styles[props.deviceType])}>
          <ArticleHeading2 as="h5">2. Подписание документов</ArticleHeading2>
          <UIText1 as="p">
            Подписание документов будет происходить в сторонних приложениях партнёров в зависимости от выбранного
            способа активации подписи:
          </UIText1>
          <DealStepModalCardList>
            <DealStepModalCard
              imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/goskey_x1.png"
              imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/goskey_x2.png"
              onClick={() => props.onCardClick('goskey')}
            >
              Госключ
            </DealStepModalCard>
            <DealStepModalCard
              imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/sign_me_x1.png"
              imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/sign_me_x2.png"
              onClick={() => props.onCardClick('signMe')}
            >
              Sign Me
            </DealStepModalCard>
          </DealStepModalCardList>
          <ArticleParagraph1>
            Когда все участники сделки активируют подписи, персональный помощник направит документы на подписание в
            {NBSP}выбранное приложение.
          </ArticleParagraph1>
        </li>

        <li className={cn(styles['step'], styles[props.deviceType])}>
          <ArticleHeading2 as="h5">3. Встреча с банком</ArticleHeading2>
          <UIText1 as="p">
            По закону 218-ФЗ, чтобы провести успешную регистрацию электронной сделки, необходим документ, подтверждающий
            участие банка в{NBSP}сделке.
          </UIText1>
          <UIText1 as="p">Для наших сделок такие документы предоставляет наш банк-партнёр.</UIText1>
          <UIText1 as="p">
            Представитель банка-партнёра по{NBSP}видеосвязи удостоверит участников сделки и передаст нам документ.
          </UIText1>
        </li>

        <li className={cn(styles['step'], styles[props.deviceType])}>
          <ArticleHeading2 as="h5">4. Взаиморасчёты</ArticleHeading2>
          <UIText1 as="p">Взаиморасчёты будут производиться в зависимости от выбранного способа:</UIText1>
          <DealStepModalCardList>
            {props.isAccreditiveActive && (
              <DealStepModalCard
                imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/sber_x1.png"
                imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/sber_x1.png"
                onClick={() => props.onCardClick('lc')}
              >
                Аккредитив от СберБанка
              </DealStepModalCard>
            )}
            {props.isSbrEnabled && (
              <DealStepModalCard
                imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/cian_x1.png"
                imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/cian_x2.png"
                onClick={() => props.onCardClick('sbr')}
              >
                Сервис безопасных расчётов Циан{NBSP}&{NBSP}Tinkoff
              </DealStepModalCard>
            )}
            <DealStepModalCard
              imageX1="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/suitcase_x1.png"
              imageX2="https://static.cdn-cian.ru/frontend/e2e-form-assets/promo/deal_steps_v2/suitcase_x2.png"
              onClick={() => props.onCardClick('other')}
            >
              Самостоятельные расчёты
            </DealStepModalCard>
          </DealStepModalCardList>
        </li>
      </ol>
    </>
  );
};

const DocumentSigningStepModalContentWithDeviceType = withDeviceType(DocumentSigningStepModalContent);
export { DocumentSigningStepModalContentWithDeviceType as DocumentSigningStepModalContent };
